<template>
    <div class="timeline-items">
        <div class="row mt-3 mb-2">
            <div class="col-12 timeline-col">
                <div class="blockrow">
                    <draggable @change="saveOrder()"
                               :list="ceremony.blocks"
                               :filter="draggableFilter"
                               :group="{ name: 'blocks' }"
                               :handle="getDragHandle()"
                               :force-fallback="true"
                               style="cursor: grab; user-select: none;"
                               :move="checkMove">
                        <transition-group>
                            <div class="block-timeline"
                                 v-for="block in ceremony.blocks"
                                 v-if="!isFixedBlock(block)"
                                 :key="block.id"
                                 :type="block.type"
                                 :class="getBlockClass(block)">
                                <div class="block-handle"
                                     :v-tooltip="block.type !== 5 ? $t('blocks.drag_to_order') : null"
                                     :type="block.type"
                                     v-if="getDragHandle() === '.block-handle'">
                                <span class="block-name">
                                    <icon name="grip-lines"/>
                                </span>
                                </div>

                                <div class="block-timeline-inner"
                                     @click="selectBlock(block)"
                                     :key="block.id"
                                     :type="block.type"
                                     :v-tooltip="$t('blocks.click_to_edit') + (getDragHandle() === '.block-timeline-inner' && block.type !== 5 ? ' ' + $t('blocks.or_drag_to_order') : '')">
                                <span class="block-name">
                                    {{ getDragHandle() === '.block-handle' ? block.name.substr(0, 20) : block.name }}
                                </span>

                                    <icon v-if="hasBlockErrors(block)" class="block-error" name="exclamation-circle"></icon>

                                    <span class="block-duration" v-if="canShowDurationForBlock(block)">
                                    <icon name="clock"></icon>
                                    {{
                                            (isDurationGuessedForBlock(block) || isInfiniteBlock(block) ? '~' : '') + $root.minutesToTimeInput(block.total_duration)
                                        }}
                                </span>

                                    <small class="block-subtitle" v-if="isInloopUitloopBlock(block)">
                                        {{ $t('blocks.fixed_block') }} {{ getFixedBlockText(block) }}
                                    </small>
                                    <small class="block-subtitle" v-if="isCondolenceBlock(block)">
                                        {{ $t('blocks.condolence_block') }} {{ getFixedBlockText(block) }}
                                    </small>
                                    <small class="block-subtitle" v-if="isMediaBlock(block)">
                                        {{ $t('blocks.media_block') }} {{ getFixedBlockText(block) }}
                                    </small>
                                    <small class="block-subtitle" v-if="isSpeakerBlock(block)">
                                        {{ $t('blocks.speaker_block') }} {{ getFixedBlockText(block) }}
                                    </small>
                                    <small class="block-subtitle" v-if="isCallBlock(block)">
                                        {{ $t('blocks.call_block') }} {{ getFixedBlockText(block) }}
                                    </small>
                                </div>

                                <button class="btn btn-secondary btn-xs rounded-circle btn-icon-only block-add"
                                        :v-tooltip="$t('blocks.add_block')"
                                        v-if="!isLastBlock(block) && ceremony.type === 'ceremony'"
                                        @click="toggleBlockCreateModal(block)">
                                    <icon name="plus"/>
                                </button>

                                <button v-if="isLastBlock(block) && ceremony.state !== 2"
                                        class="btn btn-company btn-icon-only block-add btn-xs rounded-circle"
                                        :disabled="isSubmitted()"
                                        :v-tooltip="ceremony.translated_type + ' ' + $t('blocks.submit')"
                                        @click="toggleSubmitModal(block)">
                                    <icon name="check" scale="0.8"/>
                                </button>

                                <a href="#" class="trash-block"
                                   :v-tooltip="$t('blocks.delete_block')"
                                   @click="toggleDeleteModal(block)"
                                   v-if="!isInloopUitloopBlock(block) && !isCondolenceBlock(block)">
                                    <icon name="times" scale="0.8"/>
                                </a>
                            </div>
                        </transition-group>
                    </draggable>
                </div>
            </div>

            <submit-ceremony @done_loading="show_final_modal = false" :show_final_modal="show_final_modal"/>
            <uploading-modal v-if="action"
                             :action="action"
                             @accept="action = null"
                             @ignore="ignoreUploading"/>
        </div>
    </div>
</template>

<script>
    import Modal from '../../components/Modal';
    import {mapState} from 'vuex';
    import TimeInput from '../../components/TimeInput';
    import draggable from 'vuedraggable';
    import UtilMixin from '../mixins/UtilMixin';
    import SubmitCeremony from './SubmitCeremony';
    import UploadingModal from './UploadingModal';
    import {isInfiniteBlock} from '../../utils/getMaxDurationForBlock';
    import {isMobileOrTablet} from '../../utils/deviceDetect';

    export default {

        mixins: [
            UtilMixin,
        ],

        components: {
            Modal,
            TimeInput,
            draggable,
            SubmitCeremony,
            UploadingModal
        },

        props: {
            selected_block: {
                type: Object,
                required: false,
            },
            ceremony: {
                type: Object,
                required: true,
            }
        },

        data() {
            return {
                show_delete_modal: false,
                show_final_modal: false,
                show_preview_modal: false,
                show_block_options: false,
                show_modal: null,
                block_to_edit: {},
                block_to_delete: {
                    name: null,
                },
                loading: false,
                delete_loading: false,
                save_final_loading: false,
                request: {},
                formData: {
                    name: null,
                    location_id: null,
                    date: null,
                    duration: null,
                    attendee: {
                        name: null,
                        email: null
                    }
                },
                action: null,
                actionBlock: null,

                dragHandle: this.getDragHandle()
            }
        },

        computed: {
            ...mapState({
                locations: state => state.locations,
                isUploading: state => state.isUploading
            }),
            draggableFilter() {
                return this.ceremony?.company.fixed_blocks_draggable ? '' : '[type="5"]';
            }
        },

        methods: {
            isInfiniteBlock: isInfiniteBlock,

            getFixedBlockText(block) {
                if (!this.isFixedBlock(block)) {
                    return null;
                }

                if (!block.subblocks.length) {
                    return null;
                }

                return ` (${this.getFixedBlockType(block.subblocks[0].type).toLowerCase()})`;
            },

            getDragHandle() {
                if (window.innerWidth <= 767 || isMobileOrTablet()) {
                    return '.block-handle';
                }

                return '.block-timeline-inner';
            },

            canShowDurationForBlock(block) {
                const parent = block.parent_id ? block.parent : block;
                const repeatAudioSetting = parent ? this.getSettingByName('repeat_audio', parent.settings) : null;

                return !(repeatAudioSetting && repeatAudioSetting.value === '1');
            },

            isDurationGuessedForBlock(block) {
                const parent = block.parent_id ? block.parent : block;

                const hasSongs = parent && parent.files.length
                const isInloop = parent && (parent.type === 5/* 5 = type inloop*/ || parent.type === 6/* 6 = type condolence*/)

                return !hasSongs && isInloop
            },

            ignoreUploading() {
                if (!this.action || !this.actionBlock) {
                    return;
                }

                this[this.action](this.actionBlock, true);

                this.action = null;
                this.actionBlock = null;
            },

            getBlockClass(block) {
                let classNames = [];

                if (this.selected_block && block.id === this.selected_block.id) {
                    classNames.push('active');
                }

                if (this.isLastBlock(block)) {
                    classNames.push('last-block');
                }

                return classNames.join(' ');
            },

            async saveOrder() {
                const new_order = this.ceremony.blocks.map(function (block) {
                    return block.id;
                });

                try {
                    await this.$http.post(`/api/ceremonies/code/${this.ceremony.code}/blocks/save-order`, new_order)
                } catch (exception) {
                    console.log(exception);
                    console.log('Cannot save order');
                }

            },

            selectBlock(block, force = false) {
                if (this.isUploading) {
                    this.action = 'selectBlock';
                    this.actionBlock = block;
                } else {
                    this.action = null;
                }

                if (this.isUploading && !force) {
                    return
                }

                this.$emit('block_selected', block)
            },

            isFixedBlock(block) {

                return block.settings.find(setting => {
                    return setting.name === 'fixed_block'
                });
            },

            toggleBlockCreateModal(block, force = false) {
                if (this.isUploading) {
                    this.action = 'toggleBlockCreateModal';
                    this.actionBlock = block;
                } else {
                    this.action = null;
                }

                if (this.isUploading && !force) {
                    return
                }

                // emit the block we need to create after..
                this.$emit('show_block_modal', block.id);
            },

            toggleSubmitModal(block, force = false) {
                if (this.isUploading) {
                    this.action = 'toggleSubmitModal';
                    this.actionBlock = block;
                } else {
                    this.action = null;
                }

                if (this.isUploading && !force) {
                    return
                }

                this.show_final_modal = true
            },

            toggleDeleteModal(block, force = false) {
                if (this.isUploading) {
                    this.action = 'toggleDeleteModal';
                    this.actionBlock = block;
                } else {
                    this.action = null;
                }

                if (this.isUploading && !force) {
                    return
                }

                this.$emit('block_deleted', block);
            },

            checkMove(event) {
                const type = Number(event.dragged.getAttribute('type'));
                const relatedType = Number(event.related.getAttribute('type'));

                // Allow fixed blocks to be draggable when the company setting is set, except condolence blocks.
                if (this.ceremony?.company.fixed_blocks_draggable && type !== 6 && relatedType !== 6) {
                    return true;
                }

                // Abort the drag if the block type is inloop, uitloop, or condolence.
                return !(type === 5 || relatedType === 5 || type === 6 || relatedType === 6);
            },

            isLastBlock(block) {
                // Filter for only inloop, uitloop or condolence
                const [last] = [...this.ceremony.blocks.filter(block => block.type === 5 || block.type === 6)].reverse();

                if (last.id === block.id) {
                    return true;
                }
            },

            isSubmitted() {
                return (this.ceremony && this.ceremony.state_id === 2 /** submitted */)
            },

            hasBlockErrors(block) {
                return !!this.ceremony.status.errors.find(error => {
                    return error.blocks.indexOf(block.id) !== -1;
                });
            }
        }
    }

</script>

<style lang="scss">

    .timeline-items {
        overflow: hidden;
    }

    .block-timeline {
        width: 200px;
        margin-bottom: 30px;
        cursor: pointer;
        margin-right: 57px;
        float: left;
        box-shadow: 2px 3px 5px rgba(31, 45, 61, 0.2);
        border-radius: 5px;
        position: relative;

        &:before {
            content: "";
            display: inline-block;
            background-color: #eff2f7;
            height: 3px;
            margin-top: 18px;
            left: 0;
            width: 100%;
            position: absolute;
            margin-bottom: 20px;
            z-index: -10;
        }

        &.last-block:after {
            content: "";
            display: inline-block;
            background-color: #fafbfe;
            height: 3px;
            margin-top: -34px;
            margin-left: 240px;
            width: 100%;
            position: absolute;
            margin-bottom: 20px;
            z-index: -10;
        }

        .block-timeline-inner {
            display: flex;
            align-items: center;
            background-color: #fff;
            border-bottom: 15px solid #878787;
            padding: 5px;
            width: 100%;
            border-radius: 5px;
            box-shadow: 2px 3px 5px rgba(31, 45, 61, 0.2);
            position: relative;

            * {
                pointer-events: none;
            }
        }

        &.active .block-timeline-inner {
            border-bottom-color: transparent;
        }

        .block-name {
            color: #152c5b;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
            padding-right: 5px;

            @media(max-width: 767px) {
                padding-left: 28px;
            }
        }

        &.sortable-ghost {
            .block-timeline-inner {
                border-bottom-color: #d5c549;
            }
        }

        .block-duration {
            font-size: 12px;
            margin-left: auto;
            display: flex;
            align-items: center;
            width: 60px;

            .fa-icon {
                width: 12px;
                margin-right: 5px;
                fill: #718096;
            }
        }

        .block-subtitle {
            position: absolute;
            bottom: -17px;
            left: 5px;
            color: #fff;
            font-size: 12px;
        }

        .block-add {
            position: absolute;
            margin-left: 214px;
            margin-top: -50px;
        }

        .block-add {
            position: absolute;
            margin-left: 214px;
            margin-top: -50px;
        }

        .block-handle {
            position: absolute;
            z-index: 10;

            left: 0;
            bottom: 0;
            width: 100%;
            height: 15px;

            .block-name {
                padding-left: 0;
            }

            @media(max-width: 767px) {
                top: 0;
                height: 100%;
                width: auto;
                padding: 6px;
            }
        }

        .trash-block {
            position: absolute;
            margin-top: -60px;
            margin-left: 188px;
            width: 20px;
            height: 20px;
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 10px;
            color: #718096;
            box-shadow: 2px 3px 5px rgba(31, 45, 61, 0.2);
        }


        @media(max-width: 767px) {
            width: 100%;
            display: flex;
            align-items: center;
            margin-right: 0;
            position: relative;

            &:before {
                content: none;
            }

            .block-add {
                position: relative;
                margin-left: 10px;
                margin-top: 0;
            }

            .trash-block {
                right: 35px;
                margin-left: 0;
                margin-top: -50px;
            }
        }
    }

    .sortable-drag {
        &:before {
            content: none;
        }

        .trash-block,
        .block-add {
            display: none;
        }
    }

    .block-error {
        margin-right: 4px;
        color: #E3093B;
        fill: #E3093B;
    }

</style>
